import {
  Article as Ar,
  ImageEntry,
  TeaserEntry,
  Platform,
} from "../helper/interfaces";
import React, { useEffect, useState } from "react";
import { Info, XCircle, MessageSquare } from "react-feather";
import ArticleImages from "./ArticleImages";
import EventBody from "./EventBody";

import {
  AppTheme,
  ContentType,
  ImageOrientationType,
} from "../helper/enums";
import CustomImage from "../components/ui/Image";
import { formattedDateArticle } from "../helper/timeHelper";
import { getAuthorNames, getAuthors } from "../helper/author";
import SharingButton from "./shared/SharingButton";
import {
  getImageUrlFormat_16_9_600,
  getImageUrlFormat_9_16_600,
} from "../helper/images";
import { NoContent } from "./shared/NoContent";
import Outbrain from "./Outbrain";
import Comments from "./Comments";
import AdDebug from "./AdDebug";
import StickyAd from "./StickyAd";
import Link from "next/link";
import LinkedCategories from "./LinkedCategories";
import { useLoggedRouter } from "../helper/useLoggedRouter";
import ArticleBodyTeaser from "./ArticleBodyTeaser";
import Ticker from "./Ticker";
import SharingMenuNN from "./SharingMenuNN";
import CommentsDrawerNN from "./CommentsDrawerNN";
import { useRouter } from "next/router";
import CommentsNN from "./CommentsNN";
import { getFormattedCreationDateFixed } from "../helper/niceTimeHelper";
import TrackingSnowPlow from "./TrackingSnowPlow";
import AuthorProfilesNN from "./AuthorProfilesNN";
import TeaserAdMarker from "./teaserAdMarker";
import { AppContext } from "../provider/AppContextProvider";
import { normalizeTeaserTypes } from "../helper/article";

interface Data {
  entry: Ar | null;
  contentBlocked: boolean;
  showPianoOffer: () => void;
  navigate?: (id?: string, ressortId?: string) => void;
}

const vonText = "Von"; //process.env.NEXT_PUBLIC_APP_THEME === AppTheme.nb ? "Text von" : "Von";

function ArticleComponent(data: Data) {
  const article = data.entry;
  const { appInfo } = React.useContext(AppContext);

  const [showSharing, setShowSharing] = useState<boolean>(false);
  const [showHeaderImageSubline, setShowHeaderImageSubline] =
    useState<boolean>(false);
  const [isOpenComments, setIsOpenComments] = useState<boolean>(false);

  const [routerPush, routerReplace] = useLoggedRouter();
  const router = useRouter();
  const internalNavigate = async (id, ressortId) => {
    if (data.navigate) {
      data.navigate(id, ressortId);
    } else if (id) {
      await routerReplace(`/article/${article?.id}#a=${id}`)
      await routerPush(`/article/${encodeURIComponent(id)}`);
    } else if (ressortId) {
      await routerReplace(`/article/${article?.id}#a=${ressortId}`)
      await routerPush(`/article/${encodeURIComponent(ressortId)}`);
    }
  };

  const authorNames = getAuthorNames(article?.authors ?? []);
  const authorWithId = article?.authors?.filter((m) => m.id != null);
  const imageText =
    article?.images?.length && article?.images?.length > 0 ? `${article?.images?.length} Bilder` : "";

  const headerImage: ImageEntry | null =
    article?.headerImage && article?.headerImage?.type == ContentType.Image
      ? (article?.headerImage as ImageEntry)
      : null;
  const headerImageUrl = headerImage
    ? headerImage.alignment != ImageOrientationType.Portrait
      ? getImageUrlFormat_16_9_600(headerImage)
      : getImageUrlFormat_9_16_600(headerImage)
    : "";

  const isIos = appInfo?.platform === Platform.iOS;
  const iapAvailable = appInfo?.iapAvailable === true;
  const paywallClass = isIos ? "paywall_ios" : ( iapAvailable ? "paywall_and_iap" : "paywall_and");

  const isNN = process.env.NEXT_PUBLIC_APP_THEME === AppTheme.nn;
  const headerImageClass =
    headerImage && !isNN
      ? headerImage.alignment != ImageOrientationType.Portrait
        ? "aspect-w-16 aspect-h-9"
        : "aspect-w-9 aspect-h-16"
      : "";
  const headerObject =
    article?.headerImage && article?.headerImage?.type != ContentType.Image
      ? article.headerImage
      : null;

  const teaser: TeaserEntry = {
    id: article?.id ?? "",
    type: article?.type ?? ContentType.Unknown,
    creationDate: article?.creationDate ?? "",
    date: article?.creationDate ?  Date.parse(article?.creationDate) : 0,
    title: article?.title ?? "",
    ressort: "",
    subline: "",
    topline: article?.topline ?? "",
    body: article?.body ?? "",
    lead: article?.lead ?? "",
    modulePatch: "",
    links: article?.links ?? [],
    images: headerImage ? [headerImage] : [],
    paid: article?.paid === true,    
    authors: getAuthors(article),
    featured: false,
    teaserTypes: normalizeTeaserTypes(article?.teaserTypes ?? []),
    adIdentifier: article?.adIdentifier == true,
    hidePlusIcon: false
  }

  return (
    <React.Fragment>
      {isNN && article?.pageInformation?.trackingInformation && <TrackingSnowPlow
        articleId={article?.id}
        paid={article.paid === true}
        trackingInformation={article?.pageInformation?.trackingInformation}
        contentType={article.type}
      />}
      {showSharing && (
        <SharingMenuNN
          teaser={teaser}
          sharingUrl={article?.sharingUrl ?? ""}
          onClose={() => setShowSharing(false)}
        />
      )}
      {isOpenComments && article && (
        <CommentsDrawerNN
          article={article}
          data={article?.objectComments}
          onClose={() => setIsOpenComments(false)}
        />
      )}
      {data.entry && data.entry.status === 200 ? (
        <>
          <div className="article">
            {((isNN && article?.type == ContentType.Gallery) || !isNN || (!isNN && article?.adIdentifier === true)) && article && (
              <div className="article-meta">
                {!isNN && article.adIdentifier === true && (
                  <div className="">
                    <TeaserAdMarker adIdentifier={article.adIdentifier} />
                  </div>
                )}
                {article.paid && <div className="icon-nnplus" aria-label="NN+"></div>}
                {article.topline && (
                  <div className="article-meta-topline">{article.topline}</div>
                )}
                <h1 className="article-meta-title">
                  {article?.title?.replace(/\u00a0/g, " ")}
                </h1>
              </div>
            )}
            {!isNN && (
              <div className="article-meta">
                {(article?.date || authorNames) && article && (
                  <div className="article-meta-info">
                    { article.authors.length > 0 && // authorWithId?.length < 1 &&
                  <div className="article-authors">
                    <AuthorProfilesNN authors={article.authors} navigate={internalNavigate} /> 
                  </div>
                  }
                    <span className="article-date">
                      {article.date &&
                        formattedDateArticle(article.date) + " Uhr"}
                    </span>
                  </div>
                )}
              </div>
            )}
            {article?.type !== ContentType.Gallery && headerImage && article && (
              <div className="article-title-image-wrapper">
                <div className="article-title-image-card">
                  <div className={`article-title-image article-title-image-gradient`}>
                    <div className="image-container">
                      <CustomImage
                        src={headerImageUrl}
                        alt={headerImage.title}
                        fill
                        className="object-cover"
                      />
                      { article.paid && 
                        <div className="article-paid-info">
                          <div className="icon-nnplus" aria-label="NN+"></div>
                        </div>
                      }
                    </div>
                    {!isNN
                      ? (headerImage.subline || headerImage.credit) && (
                          <div className="article-title-image-meta">
                            <span className="article-title-image-meta-credit">
                              {" "}
                              &copy; {headerImage.credit}
                            </span>
                            <span>{headerImage.subline}</span>
                          </div>
                        )
                      : (headerImage.subline || headerImage.credit) && (
                          <div className="article-title-meta-wrapper">
                            <div className="article-title-meta-wrapper__header">
                              {/* <div className="article-back">
                            <ChevronDown onClick={() => router.back()} />
                          </div> */}
                              {process.env.NEXT_PUBLIC_APP_THEME ==
                              AppTheme.nb ? (
                                article.date && (
                                  <div className="teaser-time">
                                    {formattedDateArticle(article.date) +
                                      " Uhr"}
                                  </div>
                                )
                              ) : (
                                <></>
                              )}
                            </div>
                          </div>
                        )}
                    {isNN &&
                      (headerImage?.subline || headerImage?.credit || !article?.objectComments?.disableComments) && (
                        <div className="article-title-image-meta-credit">
                          {!showHeaderImageSubline && (
                            <>
                              {/* {" "} */}
                              <div>
                                {headerImage?.credit && 
                                  <>&copy; {headerImage.credit}</>
                                }
                              </div>
                              <div className="article-title-image-meta__icons">
                                {!article.objectComments?.disableComments && 
                                  <button
                                    className="info-icon"
                                    onClick={() => {
                                      routerReplace(`${router.asPath}#comments`);
                                      setIsOpenComments(true);
                                    }}
                                    aria-label={article.objectComments?.commentsCount > 0 ? (article.objectComments?.commentsCount > 1 ? `${article.objectComments?.commentsCount} Kommentare` : `1 Kommentar`) : "Noch keine Kommentare"}
                                  >
                                    <MessageSquare aria-label={article.objectComments?.commentsCount > 0 ? (article.objectComments?.commentsCount > 1 ? `${article.objectComments?.commentsCount} Kommentare` : `1 Kommentar`) : "Noch keine Kommentare"} />
                                    {article.objectComments?.commentsCount > 0 && 
                                      <span className="comment-count">{article.objectComments?.commentsCount}</span>
                                    }
                                  </button>
                                }
                                {headerImage?.subline && (
                                  <button
                                    className="info-icon"
                                    onClick={() => {
                                      setShowHeaderImageSubline(true);
                                    }}
                                    aria-label="Bildunterschrift anzeigen"
                                  >
                                    <Info aria-label="Bildunterschrift anzeigen" />
                                  </button>
                                )}

                              </div>
                            </>
                          )}
                          {showHeaderImageSubline && (
                            <button
                              className="article-title-image-meta__subline"
                              onClick={() => {
                                setShowHeaderImageSubline(false);
                              }}
                            >
                              <div>
                                <div>{headerImage?.subline}</div>
                              </div>

                              <XCircle aria-label="Bildunterschrift ausblenden" />
                            </button>
                          )}
                        </div>
                      )}
                  </div>
                </div>
              </div>
            )}
            {process.env.NEXT_PUBLIC_APP_THEME == AppTheme.nn &&
              <LinkedCategories article={article} navigate={internalNavigate} />
            }
            {headerObject && headerObject.type != ContentType.Unknown && (
              <div className="article-title-image-wrapper">
                <div className={` ${headerObject.type === ContentType.Image ? 'article-title-image' : ''}`}>
                  <ArticleBodyTeaser
                    teaser={headerObject as TeaserEntry}
                    navigate={() => {
                      internalNavigate((headerObject as TeaserEntry).id, null);
                    }}
                  />
                </div>
              </div>
            )}

            <div className="article-body">
              {isNN && (headerImage?.credit || headerImage?.subline || article?.adIdentifier === true) && article && (
                <div className="article-body-title-meta">
                  {article.adIdentifier === true && (
                    <div className="">
                      <TeaserAdMarker adIdentifier={article.adIdentifier === true} />
                    </div>
                  )}
                  {article?.topline && (
                    <div
                      className="article-title-image-meta article-title-image-meta-topline"
                      data-text={`${article?.topline}`}
                    />
                  )}
                  {article?.title && (
                    <div
                      className="article-title-image-meta"
                      data-text={article?.title}
                    />
                  )}
                </div>
              )}
              {article?.sharingUrl && article?.showSocialToolbar && article && (
                <div className="article-sharing">
                  <SharingButton
                    className="article-sharing__button"
                    url={article.sharingUrl}
                    type="email"
                    title={article.title}
                  />
                  <SharingButton
                    className="article-sharing__button"
                    url={article.sharingUrl}
                    type="whatsapp"
                  />
                  <SharingButton
                    className="article-sharing__button"
                    url={article.sharingUrl}
                    type="facebook"
                  />
                  <SharingButton
                    className="article-sharing__button"
                    url={article.sharingUrl}
                    type="x"
                  />
                  <SharingButton
                    className="article-sharing__button"
                    url={article.sharingUrl}
                    type="copy"
                  />
                </div>
              )}

              {/* {isNN && (
                <ArticleRessortsNN ressorts={ressorts} />
              )} */}

              {article?.lead && article && (
                <div className="article-lead">
                  {article.location ? (
                    <>
                      <span className="article-lead__location">
                        {article.location}
                      </span>{" "}
                      - {article.lead}
                    </>
                  ) : (
                    <>{article.lead}</>
                  )}
                </div>
              )}
              <div
                className={`flex items-center relative ${
                  !authorNames ? "justify-end" : "justify-between"
                } gap-6`}
              >
                {isNN && article && article.authors.length > 0 && // authorWithId?.length < 1 &&
                  <div className="article-authors">
                    <AuthorProfilesNN authors={article.authors} navigate={internalNavigate} /> 
                  </div>
                }

                <div>
                  {isNN && article && article.creationDate && (
                    <div className="teaser-info">
                      <div className="teaser-time shadow">
                        {getFormattedCreationDateFixed(article.creationDate)}
                      </div>
                      <button
                        className="teaser-menu"
                        onClick={() => setShowSharing(true)}
                        aria-label="Optionen für diesen Inhalt"
                      >
                        <span />
                        <span />
                        <span />
                      </button>
                    </div>
                  )}
                </div>
              </div>
              {!data.contentBlocked && article && (
                <EventBody article={article} navigate={internalNavigate} />
              )}
              {/* {data.contentBlocked && (
                <>
                  <div className="relative flex items-center justify-center">
                    <p className="filter blur pointer-events-none select-none">
                      #### # # ## ## ######### ## ###### #### #########
                      ######### ##### ########### ###### ##### ### ##########.
                      ############# ##### ##### ### ########## ######## ### ##
                      ####### ## ######## ##### ### # ######## #####.
                    </p>

                    <div
                      className="btn btn-primary absolute"
                      onClick={() => data.showPianoOffer()}
                    >
                      Jetzt Abo abschließen
                    </div>
                  </div>
                </>
              )} */}
              {data.contentBlocked && article && article.images.length <= 1 && (
                <div className={paywallClass}>
                </div>
              )}
            </div>

            {article && article.images.length > 1 && 
            <>
              <ArticleImages article={article} contentBlocked={data.contentBlocked}/>
              {data.contentBlocked && (
                  <div className={paywallClass}>
                  </div>
                )}
            </>
            }
          </div>
          {article && (article.sportsTicker ||
            article.sportsMatches ||
            article.sportsTables) && (
            <Ticker
              sportsTicker={article.sportsTicker}
              sportsMatches={article.sportsMatches}
              sportsTables={article.sportsTables}
            />
          )}
          {process.env.NEXT_PUBLIC_APP_THEME != AppTheme.nn &&
            <>
              <div className="piano_inline_experience"></div>
              <LinkedCategories article={article} navigate={internalNavigate} />
            </>
          }

          {process.env.NEXT_PUBLIC_APP_THEME == AppTheme.nn && (
            <>
              <div className="piano_inline_experience"></div>
              <div className="article-back-home-button">
              <Link
                key={`back-to-home`}
                href={`/`}
                className="btn btn-primary btn-discreet"
                onClick={() => {
                  routerPush("/");
                }}
                >
                  Zur Startseite
                </Link>
              </div>
            </>
          )}

          {article && article.pageInformation?.adInformation?.hideRecommendationAd ==
            false && <Outbrain url={article.sharingUrl} />}
          {isNN && article && (
            <CommentsNN
              id="comments"
              data={article.objectComments}
              onClick={() => setIsOpenComments(true)}
            />
          )}
          {!isNN && article && (
            <Comments entry={article} />
          )}
          <AdDebug />
          <StickyAd />
        </>
      ) : (
        <NoContent status={data.entry?.status.toString()} text="Kein Inhalt" />
      )}
    </React.Fragment>
  );
}

export default ArticleComponent;
